import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionWorkloadService } from '../services/workload.service';
import type { ConstructionWorkload, WorkloadFilters } from '../types/construction-workload.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useConstructionWorkloads(companyId: string, initialFilters: WorkloadFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<WorkloadFilters>({
    status: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['construction-workloads', companyId, filters],
    queryFn: () => ConstructionWorkloadService.getWorkloads(companyId, filters),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ConstructionWorkload>) => ConstructionWorkloadService.createWorkload(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-workloads'] });
      toast.success('Khởi tạo Workload thi công thành công');
    },
  });

  return {
    workloads: query.data?.data || [],
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    filters,
    setFilters,
    createWorkload: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

export function useConstructionWorkloadDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-workloads', 'detail', id],
    queryFn: () => ConstructionWorkloadService.getWorkloadDetail(id!),
    enabled: !!id,
  });

  const itemsQuery = useQuery({
    queryKey: ['construction-workloads', 'items', id],
    queryFn: () => ConstructionWorkloadService.getWorkloadItems(id!),
    enabled: !!id,
  });

  const statsQuery = useQuery({
    queryKey: ['construction-workloads', 'stats', id],
    queryFn: () => ConstructionWorkloadService.getWorkloadStats(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ConstructionWorkload>) => ConstructionWorkloadService.updateWorkload(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-workloads'] });
      queryClient.invalidateQueries({ queryKey: ['construction-workloads', 'detail', id] });
      toast.success('Cập nhật Workload thành công');
    },
  });

  return {
    workload: query.data,
    items: itemsQuery.data || [],
    stats: statsQuery.data,
    isLoading: query.isLoading || itemsQuery.isLoading || statsQuery.isLoading,
    updateWorkload: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
